import { TransactionListHeaderComponent } from "../transaction-list-header.component";
import { Component } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { Wallet } from "@models/interfaces";
import { WindowResizeService } from "@core-services/window-resize.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { FiatTransaction, IndexTransaction, TradableTransaction, Transaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { isEarnOnFiatTagComparisonHelper } from "@modules/transaction/transaction.utils";

@Component({
	selector: "transaction-list-header-template",
	templateUrl: "./transaction-list-header-template.component.html",
	styleUrls: ["./transaction-list-header-template.component.scss"],
	standalone: false
})
export class TransactionListHeaderTemplateComponent extends TransactionListHeaderComponent {
	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected accountService: AccountService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected contactService: ContactService,
		protected masterdataService: MasterdataService,
		protected payaccountService: PayAccountService,
		private windowResizeService: WindowResizeService,
		protected translationService: TranslationService
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			contactService,
			masterdataService,
			payaccountService,
			translationService
		);
	}

	// check for tag name (MMF)
	public isEarnOnFiatElement(transaction: Transaction): boolean {
		const trade = transaction?.getTrade();
		return isEarnOnFiatTagComparisonHelper(trade?.getTags());
	}

	public get isMobileWindow(): boolean {
		return (
			this.windowResizeService.isMobile(this.getMobileThreshold()) ||
			this.hasMultipleTagsPerListItem(true) ||
			this.hasLongSingleTag() ||
			(this.hasLongTradableName() && this.canOpen)
		);
	}

	private getMobileThreshold(): number {
		if (this.canOpen) {
			return this.hasMultipleTagsPerListItem() ? 780 : 620;
		}
		return 635;
	}

	private isTradableTx(): boolean {
		return (
			this.transaction instanceof TradableTransaction ||
			(this.transaction instanceof FiatTransaction &&
				this.transaction.getTagShortNames().join().includes("stock_exchange"))
		);
	}

	public isIndexDetail(): boolean {
		return (this.transaction instanceof IndexTransaction || this.transaction.isIndex()) && !this.canOpen;
	}

	public isDeletedWallet(isFromWallet: boolean = true): boolean {
		const wallet = isFromWallet ? this.getFromWallet() : this.getToWallet();
		return wallet instanceof TradableWallet && wallet.isDeleted();
	}

	public getFromWallet(): Wallet {
		if (this.transaction.getType() === "buy") {
			return this.fiatWallet;
		}
		if (this.transaction.getType() === "sell" || this.isInternal) {
			return this.tradableWallet;
		}
		return null;
	}

	public getToWallet(): Wallet {
		if (this.transaction.getType() === "buy" || this.transaction.isRefund()) {
			return this.tradableWallet;
		}
		if (this.isInternal) {
			return this.relatedTradableWallet;
		}
		if (this.transaction.getType() === "sell") {
			return this.fiatWallet;
		}
		return null;
	}

	public showTradeWalletName(): string {
		if (this.isTradableTx()) {
			if (this.transaction.hasTrade()) {
				if (this.transaction.getTrade().isVoucher) {
					return this.translationService.translate("bitpandatogo_primary_field");
				}
				if (this.getGiveawayReward()) {
					return "Bitpanda";
				}
			}
			if (this.fiatWallet) {
				return this.fiatWallet.getName();
			}
			return "Bitpanda";
		}
		if (this.isTrxMarginTrading() && !this.tradableWallet) {
			const tags = this.transaction.getTransactionTags();
			let walletName = "Bitpanda";
			tags.forEach((tag) => {
				if (
					(tag.attributes.short_name.includes("repay") && this.transaction.getType() === "sell") ||
					(tag.attributes.short_name.includes("open") && this.transaction.getType() === "buy")
				) {
					walletName = `${this.getAssetNameorSymbol()} ${this.translationService.translate("margin_wallet").toLowerCase()}`;
				}
			});
			return walletName;
		}

		return this.tradableWallet ? this.tradableWallet.getName() : "";
	}

	public showTxItemTitle(): boolean {
		return this.hasTrade() && !this.transaction.isIndex() && !this.isFiatDebitCardTx();
	}

	public isFromPrefix(): boolean {
		return (
			(this.transaction.isType("buy") && this.isTradableTx()) ||
			(this.transaction.isType("sell") && !this.isTradableTx())
		);
	}

	public isIotaWarning(): boolean {
		if (this.isTradableTx()) {
			return (
				this.tradable.isIota() &&
				(<TradableTransaction>this.transaction).isExternal() &&
				this.transaction.isType("deposit") &&
				!this.canOpen
			);
		}
		return false;
	}

	public getAssetNameorSymbol(): string {
		if (this.tradable?.isToken()) {
			return this.tradable.getName();
		}
		// For cases where the tradable doesn't exist (yet), " " will be interpolated in the translation
		return this.tradable?.getSymbol(false, true) ?? " ";
	}
}
